/**
 * Import completion OBSERVER (pure).
 *
 * The floating import panel used to learn that a job had finished from ONE
 * source only: the SSE `sync_completed` event. A job that finished before the
 * subscription was live, between two events, or while the stream was down,
 * never left `activeSyncs` — the card sat frozen at `0 registros / 0%` while
 * the API had long since reported `completed / 100%` — and the user got no
 * confirmation at all.
 *
 * This module decides, from the polled `/imports/sync/status` snapshot alone,
 * which imports reached a TERMINAL state. `activeSyncs` is the backend's
 * `status IN ('pending','processing')` set, so a job that leaves that set has
 * finished by definition; `recentSyncs` / `failedSyncs` carry the outcome.
 * Completion is therefore OBSERVED, never inferred from an event that may
 * never arrive — SSE only makes the same conclusion arrive sooner.
 *
 * Deliberately honest: a job whose terminal state cannot be read from the
 * snapshot (not in recentSyncs, or `cancelled`) produces NO outcome. We never
 * fabricate a green "success" for a job we did not actually see succeed.
 */

import type { SyncStatus } from '@/platform/sync/types'

export interface ObservedImportOutcome {
  jobId: string
  /** The import source, when the job has one (a CSV upload does not): what a retry restarts. */
  sourceId?: string | null
  sourceName: string
  /** Completion/failure instant, falling back to the observation instant. */
  at: Date
  outcome: 'completed' | 'failed'
  /** Only set for `failed` — the sanitized backend message when available. */
  errorMessage?: string
}

export interface SelectObservedImportOutcomesParams {
  /** Latest `/imports/sync/status` snapshot (null before the first fetch). */
  status: SyncStatus | null
  /** Job ids that were active in the PREVIOUS snapshot this panel saw. */
  previouslyActiveJobIds: ReadonlySet<string>
  /** Job ids already surfaced to the user (never report one twice). */
  alreadyReportedJobIds: ReadonlySet<string>
  /**
   * Panel mount instant. A job that completed BEFORE the user opened this
   * session is history, not a confirmation — only newer completions count.
   */
  observedSince: Date
  /** Injected so the fallback timestamp stays deterministic in tests. */
  now: Date
}

/** Parse an ISO-8601 timestamp, returning null when absent/unparseable. */
function parseIsoDate(value: string | null | undefined): Date | null {
  if (typeof value !== 'string' || value.trim() === '') return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function selectObservedImportOutcomes(
  params: SelectObservedImportOutcomesParams,
): ObservedImportOutcome[] {
  const { status, previouslyActiveJobIds, alreadyReportedJobIds, observedSince, now } = params
  if (!status) return []

  // The store is `persist`ed, so a snapshot rehydrated from an OLDER shape can
  // be missing a list entirely. Default each one instead of trusting the type:
  // this component is mounted on every authenticated page, and a TypeError here
  // would blank the whole app.
  const activeSyncs = status.activeSyncs ?? []
  const recentSyncs = status.recentSyncs ?? []
  const failedSyncs = status.failedSyncs ?? []

  const currentlyActiveJobIds = new Set(activeSyncs.map((sync) => sync.jobId))
  const recentByJobId = new Map(recentSyncs.map((sync) => [sync.jobId, sync]))
  const failedByJobId = new Map(failedSyncs.map((sync) => [sync.jobId, sync]))

  const outcomes = new Map<string, ObservedImportOutcome>()

  const remember = (outcome: ObservedImportOutcome): void => {
    if (alreadyReportedJobIds.has(outcome.jobId)) return
    if (currentlyActiveJobIds.has(outcome.jobId)) return // re-queued: still running
    if (!outcomes.has(outcome.jobId)) outcomes.set(outcome.jobId, outcome)
  }

  // (1) Jobs this panel WATCHED running that have now left the active set.
  //     Their terminal state is read from the same snapshot — a job we cannot
  //     resolve is skipped rather than guessed.
  for (const jobId of previouslyActiveJobIds) {
    if (currentlyActiveJobIds.has(jobId)) continue

    const failed = failedByJobId.get(jobId)
    if (failed) {
      remember({
        jobId,
        sourceId: failed.sourceId,
        sourceName: failed.sourceName,
        at: parseIsoDate(failed.failedAt) ?? now,
        outcome: 'failed',
        errorMessage: failed.error?.message,
      })
      continue
    }

    const recent = recentByJobId.get(jobId)
    // 'cancelled' / 'paused' are neither a success nor a failure — stay silent.
    if (recent?.status === 'completed') {
      remember({
        jobId,
        sourceName: recent.sourceName,
        at: parseIsoDate(recent.completedAt) ?? now,
        outcome: 'completed',
      })
    } else if (recent?.status === 'failed') {
      remember({
        jobId,
        sourceId: recent.sourceId,
        sourceName: recent.sourceName,
        at: parseIsoDate(recent.completedAt) ?? now,
        outcome: 'failed',
      })
    }
  }

  // (2) Jobs that finished so fast the panel never saw them in `activeSyncs`
  //     (a small CSV can complete before the post-commit status fetch lands).
  //     Bounded to this session by `observedSince` so old history is not
  //     replayed as a fresh confirmation on every page load.
  for (const recent of recentSyncs) {
    if (recent.status !== 'completed') continue
    const completedAt = parseIsoDate(recent.completedAt)
    if (!completedAt || completedAt.getTime() < observedSince.getTime()) continue
    remember({
      jobId: recent.jobId,
      sourceName: recent.sourceName,
      at: completedAt,
      outcome: 'completed',
    })
  }

  return [...outcomes.values()]
}

/** Job ids currently in `pending`/`processing` — the panel's watch list. */
export function selectActiveJobIds(status: SyncStatus | null): Set<string> {
  return new Set((status?.activeSyncs ?? []).map((sync) => sync.jobId))
}
